import React, { useState, useEffect } from 'react';
import { supplierOffersAPI, SupplierOffer } from '../../services/api';
import { PlusIcon, DocumentTextIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import SupplierOfferForm from './SupplierOfferForm';
import SupplierOfferDetail from './SupplierOfferDetail';
import { Card, CardBody, Button, Badge, Spinner, EmptyState } from '../ui';

const SupplierOffers: React.FC = () => {
  const [offers, setOffers] = useState<SupplierOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<SupplierOffer | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const data = await supplierOffersAPI.getAll();
      setOffers(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch supplier offers');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedOffer(null);
    setIsFormOpen(true);
  };

  const handleEdit = (offer: SupplierOffer) => {
    setSelectedOffer(offer);
    setIsFormOpen(true);
  };

  const handleViewDetails = (offer: SupplierOffer) => {
    setSelectedOffer(offer);
    setIsDetailOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await supplierOffersAPI.delete(id);
      setSuccess('Supplier offer deleted successfully');
      fetchOffers();
      setTimeout(() => setSuccess(''), 3000);
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete supplier offer');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleFormSuccess = () => {
    setSuccess('Supplier offer saved successfully');
    fetchOffers();
    setTimeout(() => setSuccess(''), 3000);
  };

  const getStatusVariant = (status: string): 'warning' | 'success' | 'error' | 'neutral' => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'ACCEPTED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'EXPIRED':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">Supplier Offers</h2>
          <p className="mt-1 text-sm text-neutral-600">Historical reference of supplier offers</p>
        </div>
        <Button
          variant="primary"
          onClick={handleAdd}
          leftIcon={<PlusIcon className="h-5 w-5" />}
        >
          Add Offer
        </Button>
      </div>

      {error && (
        <div className="mb-4 bg-error-50 border border-error-200 text-error-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-success-50 border border-success-200 text-success-600 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      <Card variant="elevated">
        <CardBody className="p-0">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Unit Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Total Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Valid Until
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {offers.map((offer) => (
                <tr key={offer.id} className="hover:bg-neutral-50 cursor-pointer transition-colors">
                  <td
                    onClick={() => handleViewDetails(offer)}
                    className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900"
                  >
                    {offer.supplier?.name || 'Unknown'}
                  </td>
                  <td
                    onClick={() => handleViewDetails(offer)}
                    className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900"
                  >
                    {offer.productName}
                  </td>
                  <td
                    onClick={() => handleViewDetails(offer)}
                    className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500"
                  >
                    {offer.quantity}
                  </td>
                  <td
                    onClick={() => handleViewDetails(offer)}
                    className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900"
                  >
                    {offer.currency} {offer.unitPrice}
                  </td>
                  <td
                    onClick={() => handleViewDetails(offer)}
                    className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900"
                  >
                    {offer.currency} {offer.totalPrice}
                  </td>
                  <td
                    onClick={() => handleViewDetails(offer)}
                    className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500"
                  >
                    {offer.validUntil
                      ? new Date(offer.validUntil).toLocaleDateString()
                      : 'No expiry'}
                  </td>
                  <td
                    onClick={() => handleViewDetails(offer)}
                    className="px-6 py-4 whitespace-nowrap"
                  >
                    <Badge variant={getStatusVariant(offer.status)}>
                      {offer.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(offer)}
                      className="text-primary-600 hover:text-primary-900 mr-3"
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    {deleteConfirm === offer.id ? (
                      <div className="inline-flex items-center space-x-2">
                        <button
                          onClick={() => handleDelete(offer.id)}
                          className="text-error-600 hover:text-error-900 text-xs font-medium"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-neutral-600 hover:text-neutral-900 text-xs font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(offer.id)}
                        className="text-error-600 hover:text-error-900"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {offers.length === 0 && (
        <EmptyState
          icon={<DocumentTextIcon className="h-12 w-12" />}
          title="No supplier offers"
          description="Get started by adding supplier offers."
        />
      )}

      <SupplierOfferForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSuccess={handleFormSuccess}
        offer={selectedOffer}
      />

      <SupplierOfferDetail
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        offer={selectedOffer}
      />
    </div>
  );
};

export default SupplierOffers;

